import React, { useEffect, useState } from "react";
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import BusinessLoanPDF from './BusinessLoanPDF';
import { API_BASE_URL } from "../../config";
import { toRupees } from "../../utils/money";

// The member's real EMI chart when it exists (loanstatement, rupees); otherwise
// a preview from the same backend service that will generate it. Returns null
// if neither is available, so the PDF never prints a made-up schedule.
const fetchEmiSchedule = async (proposedLoanDetailsData) => {
  const { memberId, loanAmount, tenureInMonths, rateOfInterest, emiDate } = proposedLoanDetailsData || {};

  if (memberId) {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/getModelData/loanstatement?filter=${encodeURIComponent(JSON.stringify({ memberId }))}`
      );
      const rows = res.ok ? (await res.json()).data || [] : [];
      if (rows.length > 0) {
        // Balance is recomputed from the planned principal: the stored
        // `outstanding` column moves as instalments are collected.
        const sorted = [...rows].sort((a, b) => Number(a.dueNo) - Number(b.dueNo));
        let balance = sorted.reduce((sum, r) => sum + (Number(r.principal) || 0), 0);
        return sorted.map((r) => {
          balance -= Number(r.principal) || 0;
          return {
            dueNo: r.dueNo,
            principal: Number(r.principal) || 0,
            interest: Number(r.interest) || 0,
            total: Number(r.total) || 0,
            outstanding: Math.max(0, balance),
          };
        });
      }
    } catch (err) {
      console.error("Loan statement fetch failed:", err);
    }
  }

  if (!loanAmount || !tenureInMonths || !rateOfInterest) return null;
  try {
    const params = new URLSearchParams({
      amount: toRupees(loanAmount),
      tenure: tenureInMonths,
      rate: rateOfInterest,
      ...(emiDate ? { emiDate } : {}),
    });
    const res = await fetch(`${API_BASE_URL}/api/loanChartPreview?${params}`);
    return res.ok ? (await res.json()).schedule || null : null;
  } catch (err) {
    console.error("Loan chart preview failed:", err);
    return null;
  }
};

const BusinessLoanPrintView = ({
  personalDetailsData = {},
  verificationDetailsData = {},
  memberDetailsData = {},
  nomineeDetailsData = {},
  memberBusinessDetailsData = {},
  nomineeBusinessDetailsData = {},
  loanDetailsData = {},
  proposedLoanDetailsData = {},
  bankDetailsData = {},
  onBack,
}) => {
  const [isReady, setIsReady] = useState(false);
  const [emiSchedule, setEmiSchedule] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setIsReady(false);
    fetchEmiSchedule(proposedLoanDetailsData).then((schedule) => {
      if (cancelled) return;
      setEmiSchedule(schedule);
      setIsReady(true);
    });
    return () => { cancelled = true; };
  }, [proposedLoanDetailsData]);

  return (
    <div className="print-view-container">
      <div className="control-buttons">
        <button onClick={onBack} className="back-button">
          Back to Main View
        </button>
        {isReady ? (
          <PDFDownloadLink
            document={
              <BusinessLoanPDF
                personalDetailsData={personalDetailsData}
                verificationDetailsData={verificationDetailsData}
                memberDetailsData={memberDetailsData}
                nomineeDetailsData={nomineeDetailsData}
                memberBusinessDetailsData={memberBusinessDetailsData}
                nomineeBusinessDetailsData={nomineeBusinessDetailsData}
                loanDetailsData={loanDetailsData}
                proposedLoanDetailsData={proposedLoanDetailsData}
                bankDetailsData={bankDetailsData}
                emiSchedule={emiSchedule}
              />
            }
            fileName="Business_Loan_Application.pdf"
          >
            {({ loading }) => (
              <button className="print-button" disabled={loading}>
                {loading ? "Generating PDF..." : "Download PDF"}
              </button>
            )}
          </PDFDownloadLink>
        ) : (
          <button className="print-button" disabled>
            Preparing...
          </button>
        )}
      </div>

      {/* PDF Viewer */}
      {isReady && (
        <PDFViewer width="100%" height="600px">
          <BusinessLoanPDF
            personalDetailsData={personalDetailsData}
            verificationDetailsData={verificationDetailsData}
            memberDetailsData={memberDetailsData}
            nomineeDetailsData={nomineeDetailsData}
            memberBusinessDetailsData={memberBusinessDetailsData}
            nomineeBusinessDetailsData={nomineeBusinessDetailsData}
            loanDetailsData={loanDetailsData}
            proposedLoanDetailsData={proposedLoanDetailsData}
            bankDetailsData={bankDetailsData}
            emiSchedule={emiSchedule}
          />
        </PDFViewer>
      )}

      <style jsx>{`
        .print-view-container {
          font-family: 'Arial', sans-serif;
          padding: 20px;
          color: #333;
        }
        
        .control-buttons {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
        }
        
        .back-button, .print-button {
          padding: 10px 16px;
          background-color: #1976d2;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: bold;
          transition: background-color 0.3s;
        }
        
        .print-button {
          background-color: #2e7d32;
        }
        
        .print-button:disabled {
          background-color: #cccccc;
          cursor: not-allowed;
        }
        
        .back-button:hover {
          background-color: #1565c0;
        }
        
        .print-button:hover:not(:disabled) {
          background-color: #1b5e20;
        }
      `}</style>
    </div>
  );
};

export default BusinessLoanPrintView;




// import React, { useEffect, useState } from "react";
// import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

// // PDF Document Component
// const BusinessLoanPDF = ({
//   personalDetailsData = {},
//   verificationDetailsData = {},
//   memberDetailsData = {},
//   nomineeDetailsData = {},
//   memberBusinessDetailsData = {},
//   nomineeBusinessDetailsData = {},
//   loanDetailsData = {},
//   proposedLoanDetailsData = {},
//   bankDetailsData = {},
//   memberPhotosData = {},
//   nomineePhotosData = {},
// }) => {
//   const styles = StyleSheet.create({
//     page: {
//       padding: 20,
//       fontFamily: 'Helvetica',
//       color: '#333',
//     },
//     header: {
//       textAlign: 'center',
//       marginBottom: 30,
//       borderBottomWidth: 2,
//       borderBottomColor: '#1976d2',
//       paddingBottom: 15,
//     },
//     companyName: {
//       color: '#1976d2',
//       fontSize: 28,
//     },
//     documentTitle: {
//       color: '#555',
//       fontSize: 18,
//       marginTop: 5,
//     },
//     pageSection: {
//       marginBottom: 40,
//     },
//     pageTitle: {
//       backgroundColor: '#e8f4fd',
//       padding: 10,
//       borderLeftWidth: 5,
//       borderLeftColor: '#1976d2',
//       marginBottom: 20,
//       fontSize: 16,
//     },
//     section: {
//       marginBottom: 25,
//     },
//     sectionTitle: {
//       color: '#1976d2',
//       borderBottomWidth: 1,
//       borderBottomColor: '#ddd',
//       paddingBottom: 5,
//       marginBottom: 10,
//       fontSize: 14,
//     },
//     table: {
//       width: '100%',
//       borderWidth: 1,
//       borderColor: '#ddd',
//       marginBottom: 20,
//     },
//     tableRow: {
//       flexDirection: 'row',
//       borderBottomWidth: 1,
//       borderBottomColor: '#ddd',
//     },
//     tableRowEven: {
//       backgroundColor: '#f9f9f9',
//     },
//     labelCell: {
//       width: '40%',
//       padding: 10,
//       fontWeight: 'bold',
//       borderRightWidth: 1,
//       borderRightColor: '#ddd',
//     },
//     valueCell: {
//       width: '60%',
//       padding: 10,
//     },
//     noData: {
//       padding: 15,
//       textAlign: 'center',
//       color: '#777',
//       fontStyle: 'italic',
//       width: '100%',
//     },
//     image: {
//       maxWidth: 150,
//       maxHeight: 150,
//     },
//     signatures: {
//       flexDirection: 'row',
//       justifyContent: 'space-between',
//       marginTop: 40,
//     },
//     signatureBox: {
//       width: '30%',
//       textAlign: 'center',
//     },
//     signatureLine: {
//       borderTopWidth: 1,
//       borderTopColor: '#333',
//       marginTop: 40,
//     },
//     footer: {
//       marginTop: 40,
//       textAlign: 'center',
//       fontSize: 12,
//       color: '#777',
//       borderTopWidth: 1,
//       borderTopColor: '#ddd',
//       paddingTop: 10,
//     },
//   });

//   const renderTable = (title, data = {}) => {
//     console.log(`${title} data:`, data);
    
//     return (
//       <View style={styles.section}>
//         <Text style={styles.sectionTitle}>{title}</Text>
//         <View style={styles.table}>
//           {Object.keys(data).length === 0 ? (
//             <View style={styles.tableRow}>
//               <Text style={styles.noData}>No data available</Text>
//             </View>
//           ) : (
//             Object.entries(data).map(([key, value], index) => (
//               <View 
//                 style={[styles.tableRow, index % 2 === 0 ? styles.tableRowEven : null]} 
//                 key={key}
//               >
//                 <Text style={styles.labelCell}>{formatLabel(key)}</Text>
//                 <Text style={styles.valueCell}>
//                   {renderValue(value)}
//                 </Text>
//               </View>
//             ))
//           )}
//         </View>
//       </View>
//     );
//   };

//   const renderPhotos = (title, photos = {}) => {
//     console.log(`${title} photos:`, photos);

//     return (
//       <View style={styles.section}>
//         <Text style={styles.sectionTitle}>{title}</Text>
//         <View style={styles.table}>
//           {Object.keys(photos).length === 0 ? (
//             <View style={styles.tableRow}>
//               <Text style={styles.noData}>No photos available</Text>
//             </View>
//           ) : (
//             Object.entries(photos).map(([key, value], index) => (
//               <View 
//                 style={[styles.tableRow, index % 2 === 0 ? styles.tableRowEven : null]} 
//                 key={key}
//               >
//                 <Text style={styles.labelCell}>{formatLabel(key)}</Text>
//                 <View style={styles.valueCell}>
//                   {value && typeof value === 'string' && (value.startsWith('http') || value.startsWith('data:image')) ? (
//                     <Image src={value} style={styles.image} />
//                   ) : (
//                     <Text>{renderValue(value)}</Text>
//                   )}
//                 </View>
//               </View>
//             ))
//           )}
//         </View>
//       </View>
//     );
//   };

//   const formatLabel = (key) => {
//     return key
//       .replace(/([A-Z])/g, ' $1')
//       .replace(/^./, str => str.toUpperCase())
//       .trim();
//   };

//   const renderValue = (value) => {
//     if (value === null || value === undefined) return '-';
//     if (typeof value === 'object') return JSON.stringify(value);
//     return String(value);
//   };

//   return (
//     <Document>
//       <Page size="A4" style={styles.page}>
//         <View style={styles.header}>
//           <Text style={styles.companyName}>Jothilinga Nithi Limited</Text>
//           <Text style={styles.documentTitle}>Business Loan Application</Text>
//         </View>

//         <View style={styles.pageSection}>
//           <Text style={styles.pageTitle}>Personal & Verification Information</Text>
//           {renderTable("Personal Details", personalDetailsData)}
//           {renderTable("Verification Details", verificationDetailsData)}
//         </View>
//       </Page>

//       <Page size="A4" style={styles.page}>
//         <View style={styles.pageSection}>
//           <Text style={styles.pageTitle}>Member Information</Text>
//           {renderTable("Member Details", memberDetailsData)}
//         </View>
//       </Page>

//       <Page size="A4" style={styles.page}>
//         <View style={styles.pageSection}>
//           <Text style={styles.pageTitle}>Nominee Information</Text>
//           {renderTable("Nominee Details", nomineeDetailsData)}
//         </View>
//       </Page>

//       <Page size="A4" style={styles.page}>
//         <View style={styles.pageSection}>
//           <Text style={styles.pageTitle}>Business Information</Text>
//           {renderTable("Member Business Details", memberBusinessDetailsData)}
//           {renderTable("Nominee Business Details", nomineeBusinessDetailsData)}
//         </View>
//       </Page>

//       <Page size="A4" style={styles.page}>
//         <View style={styles.pageSection}>
//           <Text style={styles.pageTitle}>Loan Information</Text>
//           {renderTable("Loan Details", loanDetailsData)}
//           {renderTable("Proposed Loan Details", proposedLoanDetailsData)}
//         </View>
//       </Page>

//       <Page size="A4" style={styles.page}>
//         <View style={styles.pageSection}>
//           <Text style={styles.pageTitle}>Bank Details & Documentation</Text>
//           {renderTable("Bank Details", bankDetailsData)}
//           {renderPhotos("Member Photos", memberPhotosData)}
//           {renderPhotos("Nominee Photos", nomineePhotosData)}

//           <View style={styles.signatures}>
//             <View style={styles.signatureBox}>
//               <Text>Applicant's Signature</Text>
//               <View style={styles.signatureLine} />
//             </View>
//             <View style={styles.signatureBox}>
//               <Text>Nominee's Signature</Text>
//               <View style={styles.signatureLine} />
//             </View>
//             <View style={styles.signatureBox}>
//               <Text>Approving Officer</Text>
//               <View style={styles.signatureLine} />
//             </View>
//           </View>

//           <View style={styles.footer}>
//             <Text>This document is valid until 30 days from the date of printing</Text>
//             <Text>Document generated on: {new Date().toLocaleDateString()}</Text>
//           </View>
//         </View>
//       </Page>
//     </Document>
//   );
// };

// const BusinessLoanPrintView = ({
//   personalDetailsData = {},
//   verificationDetailsData = {},
//   memberDetailsData = {},
//   nomineeDetailsData = {},
//   memberBusinessDetailsData = {},
//   nomineeBusinessDetailsData = {},
//   loanDetailsData = {},
//   proposedLoanDetailsData = {},
//   bankDetailsData = {},
//   memberPhotosData = {},
//   nomineePhotosData = {},
//   onBack,
// }) => {
//   const [isReady, setIsReady] = useState(false);

//   useEffect(() => {
//     console.log('BusinessLoanPrintView props:', {
//       personalDetailsData,
//       verificationDetailsData,
//       memberDetailsData,
//       nomineeDetailsData,
//       memberBusinessDetailsData,
//       nomineeBusinessDetailsData,
//       loanDetailsData,
//       proposedLoanDetailsData,
//       bankDetailsData,
//       memberPhotosData,
//       nomineePhotosData,
//     });

//     const timer = setTimeout(() => {
//       setIsReady(true);
//     }, 500);
//     return () => clearTimeout(timer);
//   }, [
//     personalDetailsData,
//     verificationDetailsData,
//     memberDetailsData,
//     nomineeDetailsData,
//     memberBusinessDetailsData,
//     nomineeBusinessDetailsData,
//     loanDetailsData,
//     proposedLoanDetailsData,
//     bankDetailsData,
//     memberPhotosData,
//     nomineePhotosData,
//   ]);

//   return (
//     <div className="print-view-container">
//       <div className="control-buttons">
//         <button onClick={onBack} className="back-button">
//           Back to Main View
//         </button>
//         {isReady ? (
//           <PDFDownloadLink
//             document={
//               <BusinessLoanPDF
//                 personalDetailsData={personalDetailsData}
//                 verificationDetailsData={verificationDetailsData}
//                 memberDetailsData={memberDetailsData}
//                 nomineeDetailsData={nomineeDetailsData}
//                 memberBusinessDetailsData={memberBusinessDetailsData}
//                 nomineeBusinessDetailsData={nomineeBusinessDetailsData}
//                 loanDetailsData={loanDetailsData}
//                 proposedLoanDetailsData={proposedLoanDetailsData}
//                 bankDetailsData={bankDetailsData}
//                 memberPhotosData={memberPhotosData}
//                 nomineePhotosData={nomineePhotosData}
//               />
//             }
//             fileName="Business_Loan_Application.pdf"
//           >
//             {({ loading }) => (
//               <button className="print-button" disabled={loading}>
//                 {loading ? "Generating PDF..." : "Download PDF"}
//               </button>
//             )}
//           </PDFDownloadLink>
//         ) : (
//           <button className="print-button" disabled>
//             Preparing...
//           </button>
//         )}
//       </div>

//       <div className="data-preview">
//         <h3>Data Preview (for debugging):</h3>
//         <pre>{JSON.stringify({
//           personalDetailsData,
//           verificationDetailsData,
//           memberDetailsData,
//           nomineeDetailsData,
//           memberBusinessDetailsData,
//           nomineeBusinessDetailsData,
//           loanDetailsData,
//           proposedLoanDetailsData,
//           bankDetailsData,
//           memberPhotosData,
//           nomineePhotosData,
//         }, null, 2)}</pre>
//       </div>

//       <style jsx>{`
//         .print-view-container {
//           font-family: 'Arial', sans-serif;
//           padding: 20px;
//           color: #333;
//         }
        
//         .control-buttons {
//           display: flex;
//           gap: 10px;
//           margin-bottom: 20px;
//         }
        
//         .back-button, .print-button {
//           padding: 10px 16px;
//           background-color: #1976d2;
//           color: white;
//           border: none;
//           border-radius: 4px;
//           cursor: pointer;
//           font-weight: bold;
//           transition: background-color 0.3s;
//         }
        
//         .print-button {
//           background-color: #2e7d32;
//         }
        
//         .print-button:disabled {
//           background-color: #cccccc;
//           cursor: not-allowed;
//         }
        
//         .back-button:hover {
//           background-color: #1565c0;
//         }
        
//         .print-button:hover:not(:disabled) {
//           background-color: #1b5e20;
//         }
        
//         .data-preview {
//           margin-top: 20px;
//           padding: 10px;
//           background-color: #f0f0f0;
//           border-radius: 4px;
//           max-height: 300px;
//           overflow: auto;
//         }
        
//         .data-preview pre {
//           margin: 0;
//           font-size: 12px;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default BusinessLoanPrintView; 